import { useMemo, useState } from "react";
import { createFileRoute } from "@tanstack/react-router";
import { useQuery, useQueryClient } from "@tanstack/react-query";
import { getSource, useSources } from "../../../sources";
import { ServerSource } from "../../../sources/serverSource";
import {
  getPrefs,
  getTranscoderCapabilities,
  patchPrefs,
  type ServerPrefs,
  type TranscoderCapabilities,
} from "../../../lib/libraryAdmin";
import {
  activeDeviceLabel,
  ffmpegLabel,
  formatBytes,
  formFromPrefs,
  fieldOfProblem,
  hwDeviceOptions,
  HW_ENCODER_LABELS,
  MAX_HEIGHT_OPTIONS,
  MAX_SESSION_OPTIONS,
  PRUNE_OPTIONS,
  sameForm,
  THROTTLE_BOUNDS,
  toneMapOptionsFor,
  TRANSCODER_QUALITY_OPTIONS,
  transcoderPatch,
  transcoderToolNotice,
  type TranscoderForm,
  validateForm,
  withCurrent,
} from "../../../lib/transcoderAdmin";
import { useAdvancedSettings } from "../../../lib/settingsAdvanced";
import {
  Checkbox,
  Chip,
  ErrorNote,
  errorMessage,
  Field,
  InfoRow,
  Select,
} from "../../../components/ui";
import {
  AdvancedToggle,
  saveOnEnter,
  SheetHeader,
  SheetInset,
  SheetLoading,
  SheetNotice,
  SheetSection,
  UnsavedBar,
  useDraft,
  useLastTouched,
} from "../../../components/settings-sheet";
import { ServerSettingsGuard } from "../../../components/server-settings";

export const Route = createFileRoute("/_authed/settings/server/$serverId/transcoder")({
  component: ServerTranscoderSettings,
});

/** The inputs that can carry a line of their own, for the focus a wrong value takes. */
const INPUT_ID: Partial<Record<keyof TranscoderForm, string>> = {
  tempDir: "transcode-tempdir",
  throttle: "transcode-throttle",
};

/** A control that a revert takes away or disables hands the focus to the
 * checkbox that governs it. */
const GOVERNED_BY: Record<string, string> = {
  "transcode-tonemap-alg": "transcode-tonemap",
  "transcode-hw-encode": "transcode-hw",
  "transcode-hw-device": "transcode-hw",
};

/** What the boot probes found — shown above the knobs they gate. */
function CapabilitiesCard({ caps }: { caps: TranscoderCapabilities }) {
  const devices = caps.hwDevices;
  const activeId = caps.hwActive?.deviceId;
  const temp = caps.tempDir;
  // The server could not use ffmpeg or ffprobe at boot: its own sentence,
  // above everything else on the card (pre-beta N17).
  const toolNotice = transcoderToolNotice(caps);
  return (
    <SheetSection title="Capabilities">
      {toolNotice ? <ErrorNote error={toolNotice}>{toolNotice}</ErrorNote> : null}
      <div className="grid grid-cols-1 gap-4 sm:grid-cols-2">
        <div className="sm:col-span-2">
          <p className="rago-caps">Hardware acceleration</p>
          {Array.isArray(devices) ? (
            devices.length === 0 ? (
              <p className="mt-1 text-sm text-[var(--rago-text-3)]">
                Software only — no working GPU encoder found at startup.
              </p>
            ) : (
              <ul className="mt-2 space-y-2">
                {devices.map((d) => (
                  <li key={d.id} className="flex flex-wrap items-center gap-2 text-sm">
                    <span className="text-[var(--rago-text-1)]">{d.label}</span>
                    {d.id === activeId ? <Chip tone="accent">Active</Chip> : null}
                    {d.decode ? <Chip tone="ok">Decode</Chip> : null}
                    {d.scale ? <Chip tone="ok">Scale</Chip> : null}
                    {d.toneMap ? <Chip tone="ok">Tone map</Chip> : null}
                    {d.encoders.length ? (
                      <span className="text-xs text-[var(--rago-text-3)]">{d.encoders.join(", ")}</span>
                    ) : null}
                  </li>
                ))}
              </ul>
            )
          ) : (
            // Pre-v2 server: only the winning encoder string is available.
            <p className="mt-1 text-sm text-[var(--rago-text-1)]">
              {caps.hwEncoder
                ? (HW_ENCODER_LABELS[caps.hwEncoder] ?? caps.hwEncoder)
                : "Software only"}
            </p>
          )}
        </div>
        <InfoRow label="Transcoder" hint={caps.ffmpegPath}>
          {ffmpegLabel(caps.ffmpegVersion)}
        </InfoRow>
        <InfoRow
          label="HDR tone mapping"
          hint={
            caps.toneMapMode === "hardware"
              ? "Runs on the GPU."
              : caps.toneMapMode === "software"
                ? "Runs in software (SIMD)."
                : "This ffmpeg build has no tone-mapping filter."
          }
        >
          <Chip tone={caps.toneMapping ? "ok" : "muted"}>
            {caps.toneMapMode === "hardware"
              ? "Hardware"
              : caps.toneMapMode === "software"
                ? "Software"
                : caps.toneMapping
                  ? "Available"
                  : "Unavailable"}
          </Chip>
        </InfoRow>
        <InfoRow
          label="Dolby Vision"
          hint={
            caps.dolbyVisionStrip
              ? "Profile 7 and out-of-spec files play as HDR10 on non-DoVi players (metadata stripped on the fly)."
              : "This ffmpeg build cannot strip Dolby Vision metadata — profile 7 files transcode for non-DoVi players."
          }
        >
          <Chip tone={caps.dolbyVisionToneMap ? "ok" : "muted"}>
            {caps.dolbyVisionToneMap ? "Profile 5 tone map" : "No profile 5 tone map"}
          </Chip>
          <Chip tone={caps.dolbyVisionStrip ? "ok" : "muted"}>
            {caps.dolbyVisionStrip ? "Metadata strip" : "No metadata strip"}
          </Chip>
        </InfoRow>
        <InfoRow
          label="Subtitle burn-in"
          hint={caps.subtitleBurnIn ? undefined : "This ffmpeg build lacks the subtitles filter (libass)."}
        >
          <Chip tone={caps.subtitleBurnIn ? "ok" : "muted"}>
            {caps.subtitleBurnIn ? "Available" : "Unavailable"}
          </Chip>
        </InfoRow>
        {temp ? (
          <InfoRow
            label="Temporary directory"
            hint={
              temp.error
                ? `Set path unusable (${temp.error}) — using the default.`
                : temp.freeBytes
                  ? `${formatBytes(temp.freeBytes)} free`
                  : undefined
            }
          >
            {temp.effective}
          </InfoRow>
        ) : null}
      </div>
    </SheetSection>
  );
}

function ServerTranscoderSettings() {
  const { serverId } = Route.useParams();
  // Subscribe to the registry: getSource() is a sync snapshot, and the
  // prefs query would stay disabled forever without the hydration rerender.
  useSources();
  const source = getSource(serverId);
  const isServer = source instanceof ServerSource;
  const qc = useQueryClient();
  const [advanced] = useAdvancedSettings();

  const prefs = useQuery({
    queryKey: ["settings", "prefs", serverId],
    enabled: isServer,
    queryFn: () => getPrefs((source as ServerSource).conn),
  });
  // Boot-probe capabilities — read-only, independent of the prefs form.
  // Older servers 404 the route; the card simply stays hidden then.
  const caps = useQuery({
    queryKey: ["settings", "transcoder-caps", serverId],
    enabled: isServer,
    retry: false,
    queryFn: () => getTranscoderCapabilities((source as ServerSource).conn),
  });

  // One draft form seeded from the server's values, following them while it
  // is still clean (settings-sheet useDraft).
  const baseline = useMemo(
    () => (prefs.data ? formFromPrefs(prefs.data) : null),
    [prefs.data],
  );
  const [draft, setDraft] = useDraft(baseline, sameForm);

  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState<unknown>(null);
  const [fieldErrors, setFieldErrors] = useState<Partial<Record<keyof TranscoderForm, string>>>({});

  // The control changed last: it gets the focus back when the pill leaves.
  const touched = useLastTouched((lost) => document.getElementById(GOVERNED_BY[lost.id] ?? ""));

  const form = draft ?? baseline;
  const patch =
    baseline && form && prefs.data ? transcoderPatch(baseline, form, prefs.data) : {};
  // Not "the patch has keys": a throttle that is no number is left out of
  // the patch, and still has to raise the pill so that Save can say so.
  const dirty = Boolean(baseline && form && !sameForm(baseline, form));

  // Feature detection: a server that predates the engine has none of the
  // transcode keys, and phantom toggles would look broken.
  const supported = Boolean(prefs.data && "transcodeQuality" in prefs.data);
  const has = (k: keyof ServerPrefs) => Boolean(prefs.data && k in prefs.data);
  const showAdvancedToggle = has("transcodeTempDir") || has("transcodePruneSeconds");

  function update(p: Partial<TranscoderForm>) {
    setDraft((f) => ({ ...(f as TranscoderForm), ...p }));
    setSaved(false);
    setError(null);
    setFieldErrors((e) => {
      const next = { ...e };
      for (const k of Object.keys(p) as (keyof TranscoderForm)[]) delete next[k];
      return next;
    });
  }

  function revert() {
    setDraft(baseline);
    setError(null);
    setFieldErrors({});
  }

  // The lines go under their fields and the first takes the focus, which
  // brings it into view under the floating pill. A field that is not on the
  // page (hidden with Show advanced) has nowhere to say it: the pill's note.
  function showFieldErrors(errs: Partial<Record<keyof TranscoderForm, string>>) {
    setFieldErrors((prev) => ({ ...prev, ...errs }));
    const [first, message] = Object.entries(errs)[0] as [keyof TranscoderForm, string];
    const input = document.getElementById(INPUT_ID[first] ?? "");
    if (input) input.focus();
    else setError(new Error(message));
  }

  async function save() {
    if (!isServer || !form || saving || !dirty) return;
    const errs = validateForm(form);
    if (Object.keys(errs).length > 0) {
      showFieldErrors(errs);
      return;
    }
    if (Object.keys(patch).length === 0) {
      // Nothing to send (a key this server lacks): the page is clean.
      setDraft(baseline);
      return;
    }
    setSaving(true);
    setSaved(false);
    setError(null);
    try {
      await patchPrefs(source.conn, patch);
      await Promise.all([
        qc.invalidateQueries({ queryKey: ["settings", "prefs", serverId] }),
        qc.invalidateQueries({ queryKey: ["settings", "transcoder-caps", serverId] }),
      ]);
      setSaved(true);
    } catch (e) {
      const field = fieldOfProblem(e);
      const message =
        e && typeof e === "object" && "problem" in e
          ? ((e as { problem: { detail?: string; title?: string } }).problem.detail ??
            (e as { problem: { title?: string } }).problem.title)
          : undefined;
      if (field && message) {
        showFieldErrors({ [field]: message });
      } else {
        setError(e);
      }
    } finally {
      setSaving(false);
    }
  }

  // Enter in a text field saves the page.
  const enterSaves = saveOnEnter(() => void save());

  return (
    <ServerSettingsGuard serverId={serverId} heading="Transcoder">
      {(server) => (
        // .rago-sheet-fill: the root fills the sheet, so the pill docks at its foot.
        <div className="rago-sheet-fill">
          <SheetHeader
            title="Transcoder"
            action={supported && showAdvancedToggle ? <AdvancedToggle /> : null}
          />

          {prefs.isLoading || !form ? (
            // A load that failed says so below instead of spinning for good.
            prefs.error ? null : (
              <SheetLoading />
            )
          ) : !supported ? (
            <SheetNotice title="Transcoding">
              This server doesn't support transcoding yet — update rago-server.
            </SheetNotice>
          ) : (
            // Every control's change bubbles to here: the one changed last.
            <div onChange={touched.onChange}>
              {caps.data ? <CapabilitiesCard caps={caps.data} /> : null}

              <SheetSection title="Transcoding">
                <Field
                  label="Transcoder quality"
                  htmlFor="transcode-quality"
                  hint="Software (CPU) encoding only — hardware encoders ignore this."
                >
                  <Select
                    id="transcode-quality"
                    value={form.quality}
                    onChange={(v) => update({ quality: v as TranscoderForm["quality"] })}
                  >
                    {TRANSCODER_QUALITY_OPTIONS.map(([value, label]) => (
                      <option key={value} value={value}>
                        {label}
                      </option>
                    ))}
                  </Select>
                </Field>

                {advanced && has("transcodeTempDir") ? (
                  <Field
                    label="Transcoder temporary directory"
                    htmlFor="transcode-tempdir"
                    hint="Applies to new transcodes. Leave empty for the default. Prefer a RAM disk (e.g. /dev/shm/rago-transcode); never a media folder or network share."
                    error={fieldErrors.tempDir}
                  >
                    <input
                      id="transcode-tempdir"
                      className="rago-input"
                      value={form.tempDir}
                      placeholder={caps.data?.tempDir?.default ?? "Server default"}
                      spellCheck={false}
                      autoCapitalize="off"
                      autoCorrect="off"
                      onChange={(e) => update({ tempDir: e.target.value })}
                      onKeyDown={enterSaves}
                    />
                  </Field>
                ) : null}

                <Field
                  label="Throttle buffer"
                  htmlFor="transcode-throttle"
                  hint={`Seconds (${THROTTLE_BOUNDS.min}–${THROTTLE_BOUNDS.max}). How far ahead of playback the encoder runs before pausing.`}
                  error={fieldErrors.throttle}
                >
                  <input
                    id="transcode-throttle"
                    className="rago-input max-w-[10rem]"
                    inputMode="numeric"
                    pattern="[0-9]*"
                    value={form.throttle}
                    onChange={(e) => update({ throttle: e.target.value })}
                    onKeyDown={enterSaves}
                  />
                </Field>

                <Field
                  label="Maximum simultaneous video transcodes"
                  htmlFor="transcode-max"
                  hint="Additional video transcodes beyond the limit are refused instead of overloading the server."
                >
                  <Select
                    id="transcode-max"
                    value={String(form.maxSessions)}
                    onChange={(v) => update({ maxSessions: Number(v) })}
                  >
                    {withCurrent(MAX_SESSION_OPTIONS, form.maxSessions, (n) => String(n)).map(
                      ([n, label]) => (
                        <option key={n} value={n}>
                          {label}
                        </option>
                      ),
                    )}
                  </Select>
                </Field>
              </SheetSection>

              {has("transcodeHwAccel") ? (
                <SheetSection title="Hardware">
                  <div className="mb-4">
                    <Checkbox
                      id="transcode-hw"
                      checked={form.hwAccel}
                      onChange={(v) => update({ hwAccel: v })}
                      label="Use hardware acceleration when available"
                      hint="Hardware decode and encode when the boot probe found a working device; anything unsupported falls back to software automatically."
                    />
                  </div>
                  {has("transcodeHwEncode") ? (
                    <div className="mb-4">
                      <Checkbox
                        id="transcode-hw-encode"
                        checked={form.hwEncode}
                        disabled={!form.hwAccel}
                        onChange={(v) => update({ hwEncode: v })}
                        label="Use hardware-accelerated video encoding"
                        hint="With hardware acceleration on, this controls whether the device also encodes (off = hardware decode, software encode)."
                      />
                    </div>
                  ) : null}
                  {has("transcodeHwDevice") && Array.isArray(caps.data?.hwDevices) ? (
                    <Field
                      label="Hardware transcoding device"
                      htmlFor="transcode-hw-device"
                      hint={`Auto picks the best device verified at startup — currently ${activeDeviceLabel(caps.data)}.`}
                    >
                      <Select
                        id="transcode-hw-device"
                        value={form.hwDevice}
                        disabled={!form.hwAccel}
                        onChange={(v) => update({ hwDevice: v })}
                      >
                        {hwDeviceOptions(caps.data, form.hwDevice).map(([value, label]) => (
                          <option key={value} value={value}>
                            {label}
                          </option>
                        ))}
                      </Select>
                    </Field>
                  ) : null}
                </SheetSection>
              ) : null}

              <SheetSection title="Video">
                <div className="mb-4">
                  <Checkbox
                    id="transcode-tonemap"
                    checked={form.toneMapping}
                    onChange={(v) => update({ toneMapping: v })}
                    label="Enable HDR tone mapping"
                    hint="Converts HDR to SDR while transcoding. Without it, converted HDR video looks dim and washed out."
                  />
                </div>
                {form.toneMapping ? (
                  <Field
                    label="Tone mapping algorithm"
                    htmlFor="transcode-tonemap-alg"
                    hint={
                      caps.data?.toneMapMode === "hardware"
                        ? "The active device tone-maps in hardware; this curve only applies when a transcode falls back to software."
                        : undefined
                    }
                  >
                    <Select
                      id="transcode-tonemap-alg"
                      value={form.toneMapAlgorithm}
                      onChange={(v) =>
                        update({ toneMapAlgorithm: v as TranscoderForm["toneMapAlgorithm"] })
                      }
                    >
                      {toneMapOptionsFor(caps.data, form.toneMapAlgorithm).map(([value, label]) => (
                        <option key={value} value={value}>
                          {label}
                        </option>
                      ))}
                    </Select>
                  </Field>
                ) : null}
                <Checkbox
                  id="transcode-disable-video"
                  checked={form.disableVideo}
                  onChange={(v) => update({ disableVideo: v })}
                  label="Disable video stream transcoding"
                  hint="Audio conversion and remuxing stay available, but playback that needs a video conversion fails instead."
                />
              </SheetSection>

              {advanced && (has("transcodePruneSeconds") || has("transcodeMaxHeight")) ? (
                <SheetSection title="Advanced">
                  {has("transcodePruneSeconds") ? (
                    <Field
                      label="Segment retention"
                      htmlFor="transcode-prune"
                      hint="How much already-played video is kept before old segments are deleted. Smaller values use less temp space."
                    >
                      <Select
                        id="transcode-prune"
                        value={String(form.pruneSeconds)}
                        onChange={(v) => update({ pruneSeconds: Number(v) })}
                      >
                        {withCurrent(PRUNE_OPTIONS, form.pruneSeconds, (n) => `${n} seconds`).map(
                          ([n, label]) => (
                            <option key={n} value={n}>
                              {label}
                            </option>
                          ),
                        )}
                      </Select>
                    </Field>
                  ) : null}
                  {has("transcodeMaxHeight") ? (
                    <Field
                      label="Maximum output resolution"
                      htmlFor="transcode-max-height"
                      hint="Caps the resolution of transcoded video. Never forces a transcode — Direct Play is unaffected."
                    >
                      <Select
                        id="transcode-max-height"
                        value={String(form.maxHeight)}
                        onChange={(v) => update({ maxHeight: Number(v) })}
                      >
                        {withCurrent(MAX_HEIGHT_OPTIONS, form.maxHeight, (n) => `${n}p`).map(
                          ([n, label]) => (
                            <option key={n} value={n}>
                              {label}
                            </option>
                          ),
                        )}
                      </Select>
                    </Field>
                  ) : null}
                  {has("transcodeVerboseLogs") ? (
                    <Checkbox
                      id="transcode-verbose"
                      checked={form.verboseLogs}
                      onChange={(v) => update({ verboseLogs: v })}
                      label="Verbose transcoder logging"
                      hint="Runs ffmpeg with verbose output and writes it to rago-transcoder.log (needs Enable verbose logging on the General page). Useful for diagnosing a failed transcode; noisy otherwise."
                    />
                  ) : null}
                </SheetSection>
              ) : null}
            </div>
          )}

          {prefs.error ? (
            <SheetInset>
              <ErrorNote error={prefs.error} />
            </SheetInset>
          ) : null}

          {/* The root's direct child (its dock rides at the sheet's foot), and
              always rendered, as UnsavedBar asks. */}
          <UnsavedBar
            dirty={dirty}
            saving={saving}
            saved={saved}
            error={error ? errorMessage(error) : null}
            onSave={() => void save()}
            onRevert={revert}
            onFocusLost={touched.focus}
          />
        </div>
      )}
    </ServerSettingsGuard>
  );
}
